type ClientLogLevel = 'debug' | 'info' | 'warn' | 'error';

const SENSITIVE_KEY = /authorization|cookie|credential|password|private.?key|secret|session|token/i;
const EMAIL = /\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi;
const BEARER_TOKEN = /\bBearer\s+[^\s,;]+/gi;
const SENSITIVE_PARAMETER =
  /([?&#]?(?:access_?token|auth_?token|token|password|secret|code)=)[^&#\s]*/gi;

function sanitizeString(value: string): string {
  return value
    .replace(EMAIL, '[REDACTED_EMAIL]')
    .replace(BEARER_TOKEN, 'Bearer [REDACTED]')
    .replace(SENSITIVE_PARAMETER, '$1[REDACTED]');
}

function sanitize(value: unknown, seen = new WeakSet<object>(), depth = 0): unknown {
  if (typeof value === 'string') return sanitizeString(value);
  if (value === null || typeof value !== 'object') return value;
  if (depth >= 5) return '[MAX_DEPTH]';
  if (seen.has(value)) return '[CIRCULAR]';
  seen.add(value);

  if (value instanceof Error) {
    return {
      name: value.name,
      message: sanitizeString(value.message),
      stack: value.stack ? sanitizeString(value.stack) : undefined,
    };
  }

  if (Array.isArray(value)) return value.map((item) => sanitize(item, seen, depth + 1));

  return Object.fromEntries(
    Object.entries(value).map(([key, item]) => [
      key,
      SENSITIVE_KEY.test(key) ? '[REDACTED]' : sanitize(item, seen, depth + 1),
    ]),
  );
}

function write(level: ClientLogLevel, args: unknown[]): void {
  const [first, ...rest] = args;
  const message =
    typeof first === 'string'
      ? sanitizeString(first)
      : first instanceof Error
        ? sanitizeString(first.message)
        : 'client_event';
  const context = first instanceof Error ? [first, ...rest] : rest;
  const detail = {
    timestamp: new Date().toISOString(),
    level,
    message,
    ...(context.length > 0 ? { context: sanitize(context) } : {}),
  };

  if (!import.meta.env.PROD || level === 'error') {
    const serialized = JSON.stringify(detail);
    if (level === 'error') console.error(serialized);
    else if (level === 'warn') console.warn(serialized);
    else if (level === 'debug') console.debug(serialized);
    else console.info(serialized);
  }

  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('app:diagnostic', { detail }));
  }
}

export const clientLogger = {
  debug: (...args: unknown[]) => write('debug', args),
  info: (...args: unknown[]) => write('info', args),
  warn: (...args: unknown[]) => write('warn', args),
  error: (...args: unknown[]) => write('error', args),
};

/** Captures errors outside React boundaries for an optional monitoring bridge. */
export function installGlobalErrorHandlers(): () => void {
  const handleError = (event: ErrorEvent) => {
    clientLogger.error('uncaught_client_error', event.error ?? event.message);
  };
  const handleRejection = (event: PromiseRejectionEvent) => {
    clientLogger.error('unhandled_promise_rejection', event.reason);
  };

  window.addEventListener('error', handleError);
  window.addEventListener('unhandledrejection', handleRejection);

  return () => {
    window.removeEventListener('error', handleError);
    window.removeEventListener('unhandledrejection', handleRejection);
  };
}
